import { FormAccessReportSetting, FormPage } from 'backend';
import { FormPageInputData, PROPERTY_TYPE } from 'shared';
import { ValidationErrors, validator } from '../validators';
import { ValidationErrorReturnType } from '../validators/validator';
import { formatDateWithTimezoneOffset } from './getFormatDate';
import { getInputValue } from './getInputValue';
import {
  getAllElements,
  getPropertyItemElement,
} from './getPropertyItemElements';
import { FormAccessReportInputItemLog } from './models';

export class InputItemAccessLogCollector {
  // 入力中の項目を管理するバッファ
  private static inputStartTimeBuffer: { [propertyId: string]: Date } = {};
  // 入力後のログを管理するバッファ
  private static inputItemAccessLogBuffer: FormAccessReportInputItemLog[] = [];
  // フォームページ(JSONスキーマに準拠する)
  private static formPage?: FormPage;
  // アクセスレポート設定
  private static accessReportSetting?: FormAccessReportSetting;
  // フォームカスタマイズかどうか（入力項目を構成する HTML Element の特定方法が変わってくる）
  private static isFormCustomize: boolean;
  // バリデータ
  private static validatorFunc: (values: FormPageInputData) => ValidationErrors;

  private static removeEventListenerFunctions: (() => void)[] = [];
  private static flushInputLogsIntervalId?: number;
  private static FLASH_INPUT_LOGS_INTERVAL_MILLIS = 10000;

  private static blurEventListenerFunctions: (() => void)[] = [];

  static initialize(
    formPage?: FormPage | null,
    accessReportSetting?: FormAccessReportSetting | null,
    isFormCustomize = false,
  ) {
    if (!formPage || !accessReportSetting) {
      return;
    }

    this.formPage = formPage;
    this.accessReportSetting = accessReportSetting;
    this.isFormCustomize = isFormCustomize;
    // エラー種別を記録できるように指定
    this.validatorFunc = validator(
      formPage,
      ValidationErrorReturnType.ERROR_TYPE,
    );

    this.setupInputEventListeners();
    this.setupFlushInputLogsInterval();
    this.setupFlushInputLogsAbondoned();
  }

  /** 各入力項目に `click`, `focus`, `blur` イベントリスナーを登録 */
  private static setupInputEventListeners() {
    if (!this.formPage || !this.accessReportSetting) {
      return;
    }
    Object.values(this.formPage.properties).forEach((propertyItem) => {
      switch (propertyItem.propertyType) {
        case PROPERTY_TYPE.staticContent:
        case PROPERTY_TYPE.fileUpload: {
          // 静的コンテンツは対象外、ファイルアップロードは別扱い
          return;
        }
        default: {
          // 続行
        }
      }

      const propertyId = propertyItem.propertyId;
      const propertyItemElement = getPropertyItemElement(
        propertyItem,
        this.isFormCustomize,
      );
      if (Object.keys(propertyItemElement).length === 0) return;

      let blurTimeout: ReturnType<typeof setTimeout>;
      const allElements = getAllElements(propertyItemElement);
      allElements.forEach((element) => {
        // `focus` イベント（入力開始を記録）
        const handleFocus = () => {
          this.recordInputStart(propertyId);
        };
        element.addEventListener('focus', handleFocus);

        const blurFunc = () => {
          // 同じ入力項目を構成する、どの HTML タグも active でなければ入力終了と判定
          if (![...allElements].some((r) => r === document.activeElement)) {
            // 入力終了
            const startTime = this.inputStartTimeBuffer[propertyId];
            delete this.inputStartTimeBuffer[propertyId];
            if (startTime) {
              const inputDuration = new Date().getTime() - startTime.getTime();
              const inputValue = getInputValue(
                propertyItem,
                propertyItemElement,
                this.isFormCustomize,
              );
              const errors = this.validatorFunc(inputValue);
              const log: FormAccessReportInputItemLog = {
                itemId: propertyId,
                accessTimestamp: formatDateWithTimezoneOffset(startTime),
                // エラー種別重複を除外（複数選択型で起こりうる）
                errorTypes: Array.from(new Set(errors?.[propertyId])) ?? [],
                inputDuration,
              };
              this.recordInputAccessLog(log);
            }
          }
        };

        // `blur` イベント（入力終了を記録）
        const handleBlur = () => {
          clearTimeout(blurTimeout);
          blurTimeout = setTimeout(blurFunc, 500); // すぐに判定すると、同じ入力項目に focus しても即 blur 扱いになってしまうため、500ミリ秒遅延させて評価している
        };
        element.addEventListener('blur', handleBlur);

        this.blurEventListenerFunctions.push(blurFunc);

        // イベント削除用リスナーを登録
        this.removeEventListenerFunctions.push(() => {
          element.removeEventListener('focus', handleFocus);
          element.removeEventListener('blur', handleBlur);
        });

        // `click` イベント（要素を `focus` する, フォームカスタマイズの Safari で radio, checkbox でフォーカスされないため）
        const handleClick = () => {
          element.focus();
        };
        if (this.isFormCustomize) {
          element.addEventListener('click', handleClick);
          this.removeEventListenerFunctions.push(() => {
            element.removeEventListener('click', handleClick);
          });
        }
      });
    });
  }

  // バッファに入力開始時間を記録するメソッド
  private static recordInputStart(propertyId: string): void {
    if (!this.inputStartTimeBuffer[propertyId]) {
      this.inputStartTimeBuffer[propertyId] = new Date();
    }
  }

  // 入力後のログを管理するバッファにログを追加（ファイルアップロード時にも利用するので、private はつけていない）
  static recordInputAccessLog(log: FormAccessReportInputItemLog): void {
    this.inputItemAccessLogBuffer.push(log);
  }

  static reset(): void {
    // 強制的に blur
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    this.blurEventListenerFunctions.forEach((blurFunc) => blurFunc());
    this.blurEventListenerFunctions = [];
    // 残りのログを送信
    this.flushInputLogs();
    this.inputStartTimeBuffer = {};
    this.inputItemAccessLogBuffer = [];
    this.removeEventListenerFunctions.forEach((removeFunc) => removeFunc());
    this.removeEventListenerFunctions = [];
    if (this.flushInputLogsIntervalId) {
      clearInterval(this.flushInputLogsIntervalId);
    }
  }

  /**
   * 入力ログバッファをサーバに送信し、送信後にバッファを空にする
   */
  private static flushInputLogs(): void {
    if (!this.accessReportSetting?.sendInputLogsEndpoint) {
      return;
    }

    if (this.inputItemAccessLogBuffer.length === 0) {
      return;
    }

    const logs = [...this.inputItemAccessLogBuffer];
    this.inputItemAccessLogBuffer = [];
    const requestBody = {
      sessionId: this.accessReportSetting.sessionId,
      schema: this.accessReportSetting.schema,
      formId: this.accessReportSetting.formId,
      pageType: this.accessReportSetting.pageType,
      pageNumber: this.accessReportSetting.pageNumber,
      userAgent: navigator.userAgent,
      inputItemLogs: logs,
    };

    const endpoint = this.accessReportSetting.sendInputLogsEndpoint;
    const blob = new Blob([JSON.stringify(requestBody)], {
      type: 'application/json',
    });
    navigator.sendBeacon(endpoint, blob);
  }

  /** ログバッファを一定期間後に flush する */
  private static setupFlushInputLogsInterval() {
    this.flushInputLogsIntervalId = window.setInterval(() => {
      this.flushInputLogs();
    }, this.FLASH_INPUT_LOGS_INTERVAL_MILLIS);
  }

  private static setupFlushInputLogsAbondoned() {
    const beforeUnloadFunc = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.blurEventListenerFunctions.forEach((blurFunc) => blurFunc());
      this.blurEventListenerFunctions = [];
      this.flushInputLogs();
    };
    const visibilityChangeFunc = () => {
      if (document.visibilityState === 'hidden') beforeUnloadFunc();
    };
    // ページ遷移時に未送信データを送信
    window.addEventListener('beforeunload', beforeUnloadFunc);
    window.addEventListener('visibilitychange', visibilityChangeFunc);

    // イベント削除用リスナーを登録
    this.removeEventListenerFunctions.push(() => {
      window.removeEventListener('beforeunload', beforeUnloadFunc);
      window.removeEventListener('visibilitychange', visibilityChangeFunc);
    });
  }
}
